import { useEffect, useState, useContext } from "react";
import { Box, Typography, Grid, Card, CardContent, Stack, Chip, Sheet, Button, Accordion, AccordionGroup, AccordionSummary, AccordionDetails, LinearProgress, Tabs, TabList, Tab, TabPanel } from "@mui/joy";
import { LayoutContext } from "../context";
import { useNavigate } from "react-router";
import { APIGetUserStatistics } from "../api";
import UserStatsCard from "../components/stats/UserStatsCard";
import FavouritesTab from "../components/stats/FavouritesTab";
import ActivityHeatmap from "../components/ActivityHeatmap";
import CardTest from "../components/ui/CardTest";
import { CaretDown, Folder } from "@phosphor-icons/react";



export default function StatsPage() {
    const { token, tests, loadUserTests, loadingTests, setGenerateTestModalOpen } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStatistics();
        loadUserTests();
    }, []);

    const loadStatistics = async () => {
        try {
            setLoading(true);
            const response = await APIGetUserStatistics(token);
            if (response.success) {
                setStatistics(response.statistics);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateTest = () => {
        setGenerateTestModalOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex' }}>
                <Box
                    component="main"
                    className="MainContent"
                    sx={{
                        px: { xs: 2, md: 6 },
                        pt: 2,
                        pb: { xs: 2, sm: 2, md: 3 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        gap: 1,
                    }}
                >
                    <Typography level="h2" component="h1">
                        Статистика
                    </Typography>
                    <Typography>Загрузка статистики...</Typography>
                </Box>
            </Box>
        );
    }

    const { user_stats, category_stats, global_stats } = statistics || {};

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 3,
                }}
            >

                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Статистика
                    </Typography>
                </Box>

                <Tabs defaultValue={0} sx={{ bgcolor: 'transparent' }}>
                    <TabList disableUnderline sx={{ gap: 0.5, mb: 2, p: 0.5, borderRadius: '12px', bgcolor: 'background.level1', width: 'fit-content',
                        [`& .Mui-selected`]: { bgcolor: 'background.surface', boxShadow: 'sm', borderRadius: '9px' } }}>
                        <Tab disableIndicator sx={{ fontWeight: 600, borderRadius: '9px' }}>Статистика</Tab>
                        <Tab disableIndicator sx={{ fontWeight: 600, borderRadius: '9px' }}>Избранные</Tab>
                    </TabList>
                    <TabPanel value={0} sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Тепловая карта активности */}
                <ActivityHeatmap token={token} />

                <Grid container spacing={3}>
                    {/* Основная статистика пользователя */}
                    <Grid xs={12}>
                        {user_stats && <UserStatsCard stats={user_stats} />}
                    </Grid>

                    {/* Статистика по категориям */}
                    {category_stats && category_stats.length > 0 && (
                        <Grid xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
                                <CardContent>
                                    <Typography level="h4" sx={{ mb: 3 }}>
                                        Статистика по категориям
                                    </Typography>

                                    <AccordionGroup sx={{ gap: 1 }}>
                                        {category_stats.map((group) => (
                                            <Accordion
                                                key={group.root_id}
                                                variant="outlined"
                                                sx={{ borderRadius: 'lg' }}
                                            >
                                                <AccordionSummary
                                                    indicator={<CaretDown size={20} />}
                                                    sx={{ borderRadius: 'lg' }}
                                                >
                                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                                        <Folder size={20} />
                                                        <Typography level="title-md" sx={{ flex: 1 }}>
                                                            {group.root_name}
                                                        </Typography>
                                                        <Chip
                                                            size="sm"
                                                            color={
                                                                group.accuracy_percentage > 64 ? "success" :
                                                                group.accuracy_percentage >= 45 ? "warning" : "danger"
                                                            }
                                                        >
                                                            {group.accuracy_percentage}%
                                                        </Chip>
                                                        <Typography level="body-sm" color="neutral">
                                                            {group.correct_answers}/{group.total_questions}
                                                        </Typography>
                                                    </Stack>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                                                        {group.topics.map((topic) => (
                                                            <Sheet
                                                                key={topic.category_id}
                                                                variant="soft"
                                                                sx={{
                                                                    p: 1.5,
                                                                    borderRadius: 'md',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 2
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography level="body-sm" fontWeight={500}>
                                                                        {topic.category_name}
                                                                    </Typography>
                                                                    <LinearProgress
                                                                        determinate
                                                                        value={topic.accuracy_percentage}
                                                                        color={
                                                                            topic.accuracy_percentage > 64 ? "success" :
                                                                            topic.accuracy_percentage >= 45 ? "warning" : "danger"
                                                                        }
                                                                        sx={{ mt: 0.5, height: 6, borderRadius: 'sm' }}
                                                                    />
                                                                </Box>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="soft"
                                                                    color={
                                                                        topic.accuracy_percentage >= 80 ? "success" :
                                                                        topic.accuracy_percentage >= 60 ? "warning" : "danger"
                                                                    }
                                                                >
                                                                    {topic.accuracy_percentage}%
                                                                </Chip>
                                                                <Typography level="body-xs" color="neutral" sx={{ minWidth: 45, textAlign: 'right' }}>
                                                                    {topic.correct_answers}/{topic.total_questions}
                                                                </Typography>
                                                            </Sheet>
                                                        ))}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </AccordionGroup>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Мои тесты */}
                <Box sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            mb: 2,
                            gap: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'start', sm: 'center' },
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography level="h3">
                            Мои тесты
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleGenerateTest}
                        >
                            Создать тест
                        </Button>
                    </Box>

                    {loadingTests ? (
                        <Typography>Загрузка тестов...</Typography>
                    ) : tests.length > 0 ? (
                        <Grid container spacing={2} columns={{ xs: 2, sm: 12, md: 12 }}>
                            {tests.map((test) => (
                                <Grid key={test.id} xs={12} sm={6} lg={4}>
                                    <CardTest test={test} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Sheet variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 'lg' }}>
                            <Typography level="h4" color="neutral" sx={{ mb: 2 }}>
                                У вас пока нет тестов
                            </Typography>
                            <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
                                Создайте свой первый тест, чтобы начать обучение
                            </Typography>
                            <Button size="lg" onClick={handleGenerateTest}>
                                Создать первый тест
                            </Button>
                        </Sheet>
                    )}
                </Box>
                    </TabPanel>
                    <TabPanel value={1} sx={{ p: 0 }}>
                        <FavouritesTab token={token} onCreateTest={() => navigate('/tests?mode=favourite')} />
                    </TabPanel>
                </Tabs>
            </Box>
        </Box>
    );
}